// The currency the pricing shows (sculpt-api PLAN-multi-currency.md C2): the organization's once it is fixed (its
// billing country is set, or it paid for something); before that, the one the URL asks for (`?currency=`, the
// website's pricing page links with it) when it is on sale, else the API's guess from the visitor's address or
// language. Nothing is converted here: each currency has its own offers and packs, under the same keys.
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import type { Organization, PricingContext } from '../types';

export interface ShownCurrency {
  currency: string;
  /** The currencies the admin may switch to; empty once the organization's is fixed or only one is on sale. */
  choices: string[];
}

export async function shownCurrency(org: Organization, requested: string | null): Promise<ShownCurrency> {
  if (org.currency_fixed) return { currency: org.currency, choices: [] };
  let context: PricingContext | null = null;
  try {
    context = await api.pricingContext();
  } catch {
    /* only a guess: the organization's currency stands */
  }
  const onSale = context?.currencies.length ? context.currencies : [org.currency];
  const asked = requested?.toUpperCase() ?? null;
  const currency = asked && onSale.includes(asked) ? asked : context && onSale.includes(context.currency) ? context.currency : org.currency;
  return { currency, choices: onSale.length > 1 ? onSale : [] };
}

/** EUR · USD · GBP, while the organization's currency is not fixed and more than one is on sale. */
export function CurrencySwitcher({ value, choices, onChange }: { value: string; choices: string[]; onChange: (currency: string) => void }) {
  const { t } = useTranslation();
  if (choices.length < 2) return null;
  return (
    <div className="flex items-center gap-2 text-[12px]" data-testid="currency-switcher">
      <span className="text-muted">{t('pricing.currency')}</span>
      <div className="flex gap-0.5 rounded-md bg-hover p-0.5" role="radiogroup" aria-label={t('pricing.currency')}>
        {choices.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={c === value}
            onClick={() => c !== value && onChange(c)}
            className={`rounded-sm px-2 py-0.5 font-mono ${c === value ? 'bg-white font-medium text-ink shadow-xs' : 'text-muted hover:text-ink'}`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}
